import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Grid,
  Pagination,
  TableCell,
  TableFooter,
  TableRow,
  Typography,
} from "@mui/material";
import { uniq } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import { getAppointmentsTableReportData } from "../../../../redux/reducers/reportSlice";
import { getClinicVets } from "../../../../redux/reducers/vetSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import {
  appointmentStatusList,
  scheduleImmediateList,
  serviceTypes,
  typeAppointemnt,
  typeList,
  visitTypes,
  yesNoBoolList,
} from "../../../../util/dropList";
import { minutesToHours } from "../../../../util/function";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";

const tableHdrs = [
  "appointmentDateTime",
  "vetName",
  "petName",
  "caseId",
  "serviceType",
  "visitType",
  "typeOfConsult",
  "modeOfBooking",
  "timeSpent",
  "delay",
  "waitTime",
  "scheduledOrImmediate",
  "rescheduled",
  "status",
  "revenue",
  "rating",
  "cancellationReason",
  "rescheduledReason",
  "assignedUsers",
];
const initSearch = {
  vetName: "",
  petName: "",
  caseId: "",
  serviceType: [],
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};
const filterByList = [
  "Appointment Date",
  "Visit Type",
  "Consultation Mode",
  "Booking Type",
  "Time Spent",
  "Delay",
  "Wait Time",
  "Scheduled/Immediate",
  "Rescheduled",
  "Status",
  "Revenue",
  "Rating",
  "Cancellation Reason",
  "Rescheduling Reason",
  "Assigned Users",
];
const dropFields = [
  "Visit Type",
  "Consultation Mode",
  "Booking Type",
  "Scheduled/Immediate",
  "Rescheduled",
  "Status",
  "Assigned Users",
];
const splitRows = [
  "Appointment Date",
  "Time Spent",
  "Delay",
  "Wait Time",
  "Revenue",
  "Rating",
];

const AppointmentsTableReport = () => {
  const dispatch = useDispatch();
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [filterValues, setFilterValues] = useState(initFilter);
  const { appointmentTable } = useSelector((state) => state?.report);
  const vets = useSelector((state) => state?.vet?.vets);
  const rowsPerPage = 10;

  useEffect(() => {
    dispatch(getClinicVets("?type=all"));
  }, [dispatch]);

  useEffect(() => {
    onApiCall(1);
  }, [
    searchValues?.vetName,
    searchValues?.petName,
    searchValues?.caseId,
    searchValues?.serviceType,
  ]);

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const reqObj = {
      type: "appointments",
      page: currentPage,
      itemSize: rowsPerPage,
    };
    if (reset) return reqObj;
    if (searchValues?.vetName?.length > 0) {
      reqObj.vetName = [searchValues?.vetName];
    }
    if (searchValues?.petName?.length > 0) {
      reqObj.petName = [searchValues?.petName];
    }
    if (searchValues?.caseId?.length > 0) {
      reqObj.caseId = [searchValues?.caseId];
    }
    if (searchValues?.serviceType?.length > 0) {
      reqObj.serviceType = searchValues?.serviceType?.map((li) => li?.value);
    }

    if (filterValues?.selectedList?.includes("Appointment Date")) {
      reqObj.startDate = moment(
        new Date(filterValues?.values?.["Start Date"] ?? new Date()),
      ).format("YYYY-MM-DD");
      reqObj.endDate = moment(
        new Date(filterValues?.values?.["End Date"] ?? new Date()),
      ).format("YYYY-MM-DD");
    }
    if (filterValues?.selectedList?.includes("Visit Type")) {
      reqObj.visitType = filterValues?.values?.["Visit Type"]?.map(
        (li) => li?.value,
      );
    }
    if (filterValues?.selectedList?.includes("Consultation Mode")) {
      reqObj.typeOfConsult = filterValues?.values?.["Consultation Mode"]?.map(
        (li) => li?.value,
      );
    }
    if (filterValues?.selectedList?.includes("Booking Type")) {
      reqObj.modeOfBooking = filterValues?.values?.["Booking Type"]?.map(
        (li) => li?.value,
      );
    }
    if (filterValues?.selectedList?.includes("Scheduled/Immediate")) {
      reqObj.immediate = filterValues?.values?.["Scheduled/Immediate"]?.map(
        (li) => li?.value,
      );
    }
    if (filterValues?.selectedList?.includes("Rescheduled")) {
      reqObj.rescheduled = filterValues?.values?.["Rescheduled"]?.map(
        (li) => li?.value,
      );
    }
    if (filterValues?.selectedList?.includes("Status")) {
      reqObj.status = filterValues?.values?.["Status"]?.map((li) => li?.value);
    }
    if (filterValues?.selectedList?.includes("Time Spent")) {
      reqObj.minTimeSpentMinutes = Number(
        filterValues?.values?.["Min Time Spent"] ?? 0,
      );
      reqObj.maxTimeSpentMinutes = Number(
        filterValues?.values?.["Max Time Spent"] ?? 0,
      );
    }
    if (filterValues?.selectedList?.includes("Delay")) {
      reqObj.minDelayMinutes = Number(filterValues?.values?.["Min Delay"] ?? 0);
      reqObj.maxDelayMinutes = Number(filterValues?.values?.["Max Delay"] ?? 0);
    }
    if (filterValues?.selectedList?.includes("Wait Time")) {
      reqObj.minWaitTimeMinutes = Number(
        filterValues?.values?.["Min Wait Time"] ?? 0,
      );
      reqObj.maxWaitTimeMinutes = Number(
        filterValues?.values?.["Max Wait Time"] ?? 0,
      );
    }
    if (filterValues?.selectedList?.includes("Revenue")) {
      reqObj.minRevenue = Number(filterValues?.values?.["Min Revenue"] ?? 0);
      reqObj.maxRevenue = Number(filterValues?.values?.["Max Revenue"] ?? 0);
    }
    if (filterValues?.selectedList?.includes("Rating")) {
      reqObj.minRating = Number(filterValues?.values?.["Min Rating"] ?? 0);
      reqObj.maxRating = Number(filterValues?.values?.["Max Rating"] ?? 0);
    }
    if (filterValues?.selectedList?.includes("Cancellation Reason")) {
      reqObj.cancelledReason = [
        filterValues?.values?.["Cancellation Reason"] ?? "",
      ];
    }
    if (filterValues?.selectedList?.includes("Rescheduling Reason")) {
      reqObj.rescheduledReason = [
        filterValues?.values?.["Rescheduling Reason"] ?? "",
      ];
    }
    if (filterValues?.selectedList?.includes("Assigned Users")) {
      reqObj.assignedUsers = (
        filterValues?.values?.["Assigned Users"] ?? []
      )?.map((li) => li?.value);
    }
    return reqObj;
  };

  const onApiCall = (pge = 1, reset = false) => {
    dispatch(getAppointmentsTableReportData(getFilterObj(pge, reset)));
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find(
        (tblHdr) => tblHdr?.name === pd,
      );
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => {
    setEditColModVsble(false);
  };

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders
      ?.filter((act) => act?.isSelected)
      ?.map((act) => {
        return act?.name;
      });
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const totalPage = Math.ceil(
    (appointmentTable?.totalRecords ?? 0) / rowsPerPage,
  );

  const handleChangePage = (e, selectedPage) => {
    onApiCall(selectedPage);
  };

  const getTableData = () => {
    return (appointmentTable?.data ?? [])?.map((li) => ({
      ...li,
      appointmentDateTime:
        li?.appointmentDateTime?.length > 0
          ? moment(new Date(li?.appointmentDateTime)).format(
              "DD MMM YYYY, HH:mm",
            )
          : null,
      rescheduled: (li?.rescheduled ?? "")?.toString(),
      typeOfConsult:
        li?.typeOfConsult === "Home" ? "Home Visit" : li?.typeOfConsult,
      revenue: Number(li?.revenue ?? 0)?.toFixed(2),
    }));
  };

  const handleOpenChart = () => {
    console.log("Log triggered");
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({
      ...searchValues,
      [name?.trim()?.replace(/,$/, "")]: value, // remove spaces & trailing commas
    });
  };

  const onFilterChange = (name, value) => {
    const reqObj = { ...filterValues, [name]: value };
    setFilterValues(reqObj);
  };

  const onReset = () => {
    flushSync(() => {
      setSearchValues(initSearch);
    });
    flushSync(() => {
      setFilterValues({ ...initFilter, selectedList: [], values: {} });
    });
    onApiCall(1, true);
  };

  const getVetList = () => {
    const reqList = (appointmentTable?.data ?? [])
      ?.map((li) => li?.vetName)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    // const reqDropList = uniqList?.map((li) => ({ name: li, valeu: li }));
    return uniqList;
  };

  const getPetList = () => {
    const reqList = (appointmentTable?.data ?? [])
      ?.map((li) => li?.petName)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    // const reqDropList = uniqList?.map((li) => ({ name: li, valeu: li }));
    return uniqList;
  };

  const getCaseIds = () => {
    const reqList = (appointmentTable?.data ?? [])
      ?.map((li) => li?.caseId?.toString())
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    // const reqDropList = uniqList?.map((li) => ({ name: li, valeu: li }));
    return uniqList;
  };

  const onApplyFilter = () => {
    onApiCall();
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const onCloseFilter = () => {
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    const reqObj = {
      ...filterValues,
      values: {
        ...filterValues?.values,
        [name?.trim()?.replace(/,$/, "")]: value,
      },
    };
    setFilterValues(reqObj);
  };

  const onFilterAdd = (value) => {
    const reqObj = { ...filterValues, added: value };
    setFilterValues(reqObj);
  };

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) {
      reqList = reqList?.filter((item) => item !== name);
    } else {
      reqList.push(name);
    }
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const onFilterReset = () => {
    const reqObj = {
      ...initFilter,
      visible: true,
      selectedList: [],
      values: {},
    };
    setFilterValues(reqObj);
  };

  const getAssignedUserList = () => {
    if (!appointmentTable?.data) return [];
    const reqList = (appointmentTable?.data ?? [])
      ?.flatMap((li) => li?.assignedUsers)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    const reqDropList = uniqList?.filter(Boolean)?.map((li) => {
      const reqList = vets?.vets?.find((vl) => vl?.doctorId === li);
      return { name: reqList?.name, value: li };
    });
    return reqDropList;
  };

  const getDropListByType = (typ) => {
    return typ === "Visit Type"
      ? visitTypes
      : typ === "Consultation Mode"
        ? typeList
        : typ === "Booking Type"
          ? typeAppointemnt
          : typ === "Scheduled/Immediate"
            ? scheduleImmediateList
            : typ === "Rescheduled"
              ? yesNoBoolList
              : typ === "Status"
                ? appointmentStatusList
                : typ === "Assigned Users"
                  ? getAssignedUserList()
                  : [];
  };

  const getFieldName = (name, type) => {
    if (name === "Appointment Date") {
      if (type === "start") return "Start Date";
      return "End Date";
    }
    if (name === "Time Spent") {
      if (type === "start") return "Min Time Spent";
      return "Max Time Spent";
    }
    if (name === "Delay") {
      if (type === "start") return "Min Delay";
      return "Max Delay";
    }
    if (name === "Wait Time") {
      if (type === "start") return "Min Wait Time";
      return "Max Wait Time";
    }
    if (name === "Revenue") {
      if (type === "start") return "Min Revenue";
      return "Max Revenue";
    }
    if (name === "Rating") {
      if (type === "start") return "Min Rating";
      return "Max Rating";
    }
  };

  const getAssignedUsers = (users = []) => {
    if (users?.length === 0 || !users) return "";
    const reqList = users?.map((li) => {
      const reqList = vets?.vets?.find((vl) => vl?.doctorId === li);
      return reqList?.name ?? "";
    });
    return reqList?.join(", ");
  };

  return (
    <>
      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomTextField
              options={getVetList()}
              value={searchValues?.vetName ?? []}
              name="vetName"
              handleChange={onSearchChange}
              label="Vet Name"
              backgroundColor={AppColors.lightBlue2}
              suggetions
              fullWidth
              // multiSelectSearchCheck
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              options={getPetList()}
              value={searchValues?.petName ?? []}
              name="petName"
              handleChange={onSearchChange}
              label="Pet Name"
              backgroundColor={AppColors.lightBlue2}
              suggetions
              fullWidth
              // multiSelectSearchCheck
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              options={getCaseIds()}
              value={searchValues?.caseId ?? []}
              name="caseId"
              handleChange={onSearchChange}
              label="Case ID"
              backgroundColor={AppColors.lightBlue2}
              suggetions
              fullWidth
              // multiSelectSearchCheck
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={serviceTypes}
              value={searchValues?.serviceType ?? []}
              name="serviceType"
              handleChange={onSearchChange}
              label="Service Type"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div
            className="flex-row-ali-cen ml20 filter cursor"
            onClick={() => onFilterChange("visible", true)}
          >
            <TuneIcon sx={{ color: AppColors.appPrimary }} />
            <Typography className="font-medium fs12 blue-color ml10">
              {filterValues?.selectedList?.length > 0
                ? `${filterValues?.selectedList?.length} `
                : ""}
              Filter
            </Typography>
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleEditColumn}
          >
            <EditOutlinedIcon sx={w20h20BlueIcon} />
            <Typography className="blue-color fs16 font-medium ml5">
              Edit columns ({crntTblHeders?.length})
            </Typography>
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleOpenChart}
          >
            <AutoGraphIcon sx={w20h20BlueIcon} />
          </div>
        </div>
      </div>
      <Typography className="font-medium fs12 gray28 ml30">
        Number of Records: {appointmentTable?.totalRecords ?? 0}
      </Typography>
      <div className="mtMin10">
        <ReportTable
          datas={getTableData()?.map((li) => ({
            ...li,
            date: `${moment(new Date(li?.appointmentDate)).format("DD MMM YYYY")}, ${li?.appointmentTime}`,
            timeSpend:
              (li?.timeSpentMinutes ?? "")?.toString()?.length > 0
                ? minutesToHours(li?.timeSpentMinutes)
                : "",
            delay:
              (li?.delayMinutes ?? "")?.toString()?.length > 0
                ? minutesToHours(li?.delayMinutes)
                : "",
            waitTime:
              (li?.waitTimeMinutes ?? "")?.toString()?.length > 0
                ? minutesToHours(li?.waitTimeMinutes)
                : "",
            assignedUsers: getAssignedUsers(li?.assignedUsers),
          }))}
          columns={crntTblHeders}
        />
        <div className="flex1-end mtMin30">
          {Math.ceil((appointmentTable?.totalRecords ?? 0) / rowsPerPage) >
          1 ? (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={12}>
                  <div className="flex1-end">
                    <Pagination
                      count={totalPage}
                      variant="outlined"
                      color="primary"
                      page={page}
                      onChange={handleChangePage}
                    />
                  </div>
                </TableCell>
              </TableRow>
            </TableFooter>
          ) : null}
        </div>
      </div>

      <CustomModal
        open={editColModVsble}
        onClose={onEditModClose}
        header="Columns"
        modal
        modalWidth={25}
      >
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <div>
              <CustomButton
                text="Save"
                smallBtn
                onClick={handleSaveTableColumn}
              />
            </div>
          </div>
        </div>
      </CustomModal>
      <CustomModal
        open={filterValues?.visible}
        onClose={onCloseFilter}
        header="Filter"
        rightModal
        modalWidth={30}
      >
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography className="gray38 fs12 txt-regular">
                Filter by:
              </Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <div>
                      <CustomCheckbox
                        checked={filterValues?.selectedList?.includes(li)}
                        onChange={() => onSelectFilter(li)}
                      />
                    </div>
                    <Typography className="txt-regular fs14 black mb5 mlMin15">
                      {li}
                    </Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div
                    className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor"
                    onClick={() => onFilterAdd(false)}
                  >
                    <AddIcon
                      sx={{ color: AppColors.appPrimary, fontSize: 15 }}
                    />
                    <Typography className="font-medium fs12 blue-color ml5">
                      Add Filter
                    </Typography>
                  </div>
                  <div>
                    {filterValues?.selectedList?.map((li) => (
                      <div className="mt20">
                        {dropFields?.includes(li) ? (
                          <CustomSelect
                            list={getDropListByType(li)}
                            value={filterValues?.values?.[li] ?? []}
                            name={li}
                            handleChange={onFilterValuesChange}
                            label={li}
                            multiSelectSearchCheck
                          />
                        ) : splitRows?.includes(li) ? (
                          <div className="flex-row-between-align-center">
                            <div className="w45Per">
                              <CustomTextField
                                placeholder={getFieldName(li, "start")}
                                fullWidth
                                name={getFieldName(li, "start")}
                                handleChange={onFilterValuesChange}
                                value={
                                  filterValues?.values?.[
                                    getFieldName(li, "start")
                                  ] ?? ""
                                }
                                label={getFieldName(li, "start")}
                                mobileDate={li === "Appointment Date"}
                                type="number"
                              />
                            </div>
                            <div className="w45Per">
                              <CustomTextField
                                placeholder={getFieldName(li, "end")}
                                fullWidth
                                name={getFieldName(li, "end")}
                                handleChange={onFilterValuesChange}
                                value={
                                  filterValues?.values?.[
                                    getFieldName(li, "end")
                                  ] ?? ""
                                }
                                label={getFieldName(li, "end")}
                                mobileDate={li === "Appointment Date"}
                                type="number"
                              />
                            </div>
                          </div>
                        ) : (
                          <CustomTextField
                            fullWidth
                            name={li}
                            value={filterValues?.values?.[li] ?? ""}
                            handleChange={onFilterValuesChange}
                            label={li}
                            placeholder={li}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <div>
                    <CustomButton
                      text="Add"
                      smallBtn
                      onClick={() => onFilterAdd(true)}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <div>
                    <CustomButton
                      text="Reset All"
                      smallBtn
                      onClick={onFilterReset}
                      tabSelectdBtn
                    />
                  </div>
                  <div className="ml20">
                    <CustomButton
                      text="Apply Filters"
                      smallBtn
                      onClick={onApplyFilter}
                    />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </>
  );
};

export default AppointmentsTableReport;
